import React from "react"
import { Box, InputLabel, Stack, Collapse } from "@mui/material"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { CustomSlider, ValueLabelComponent } from "./CustomSlider"
import { Language } from "src/utils/useLanguageList"

const SLIDER_LABELS = [
  { label: "Strict Facts", description: "No additions, only database content." },
  { label: "Fact-Based", description: "Primarily factual with slight flexibility." },
  { label: "Balanced", description: "Equal mix of facts and creativity." },
  { label: "Creative", description: "Mostly creative, but still fact-aware." },
  { label: "Unrestricted", description: "Fully open to new ideas." }
]

export interface TaxonomyPair {
  code: string
  name: string
  group: string
}

export interface ModelPair {
  model: string
  name: string
}

export interface Prompt {
  title: string
  prompt: string
}

export interface Params {
  prompts: Prompt[]
  selectedPrompt: Prompt
}

interface KmControlsProps {
  generate?: boolean
  taxonomies: TaxonomyPair[]
  selectedTaxonomy: TaxonomyPair
  setSelectedTaxonomy: (taxonomy: TaxonomyPair) => void
  lang: Language
  setLang: (lang: Language) => void
  languages: Language[]
  model: ModelPair
  setModel: (model: ModelPair) => void
  models: ModelPair[]
  params?: Params
  temperature?: number
  setTemperature?: (temperature: number) => void
  setParams?: React.Dispatch<React.SetStateAction<Params>>
}

export default function KmControls({
  generate,
  taxonomies,
  selectedTaxonomy,
  setSelectedTaxonomy,
  lang,
  setLang,
  languages,
  model,
  setModel,
  models,
  params,
  temperature,
  setTemperature,
  setParams
}: KmControlsProps) {
  const handleSelectChange = (selectedTitle: string) => {
    const selectedPrompt = params?.prompts.find((prompt) => prompt.title === selectedTitle)
    if (selectedPrompt) {
      setParams?.((prevParams: Params) => ({
        ...prevParams,
        selectedPrompt: selectedPrompt
      }))
    }
  }
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setTemperature?.(newValue as number)
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Collapse in>
        <Stack spacing={{ xs: 2, md: 1 }} direction={{ xs: "column", md: "row" }} sx={{ mb: 2 }}>
          {taxonomies && setSelectedTaxonomy && (
            <Box sx={{ width: { xs: "100%", md: "250px" } }}>
              <CustomAutocomplete
                data-testid="km-select-taxonomy"
                size="small"
                value={selectedTaxonomy}
                options={taxonomies}
                getOptionLabel={(option) => option.name}
                onChange={(e, val) => val && setSelectedTaxonomy(val as TaxonomyPair)}
                label={"Knowledge Base"}
                isOptionEqualToValue={(o1, o2) => o1.code === o2.code}
                groupBy={(option) => option.group}
                disableClearable
              />
            </Box>
          )}
          <Box sx={{ width: { xs: "100%", md: "250px" } }}>
            <CustomAutocomplete
              data-testid="km-select-language"
              size="small"
              value={lang}
              options={languages}
              getOptionLabel={(option) => option.name}
              onChange={(e, val) => val && setLang(val as Language)}
              label={"Output language"}
              isOptionEqualToValue={(o1, o2) => o1.languageId === o2.languageId}
              disableClearable
            />
          </Box>
          {models && setModel && (
            <Box sx={{ width: { xs: "100%", md: "200px" } }}>
              {model && (
                <CustomAutocomplete
                  data-testid="km-select-model"
                  size="small"
                  value={model}
                  options={models}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, val) => val && setModel(val as ModelPair)}
                  label={"Large Language Model"}
                  isOptionEqualToValue={(o1, o2) => o1.model === o2.model}
                  disableClearable
                />
              )}
            </Box>
          )}

          {generate && params && (
            <Box sx={{ width: { xs: "100%", md: "200px" } }}>
              <CustomAutocomplete
                data-testid="km-select-prompt"
                size="small"
                value={params.selectedPrompt.title}
                options={params.prompts.map((prompt) => prompt.title)}
                getOptionLabel={(option) => option}
                onChange={(e, val) => val && handleSelectChange(val as string)}
                label={"Select template"}
                isOptionEqualToValue={(o1, o2) => o1 === o2}
              />
            </Box>
          )}
          {generate && (
            <Box
              sx={{
                width: { xs: "100%", md: 200 },
                pl: { xs: 0, md: 4 },
                mt: { xs: 1, md: 0 }
              }}
            >
              <InputLabel
                sx={{
                  color: "#01294e",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "1.2em",
                  mb: { xs: 1, md: 0.5 }
                }}
              >
                {"How creative should I be?"}
              </InputLabel>
              <CustomSlider
                data-testid="km-slider"
                slots={{
                  valueLabel: ValueLabelComponent
                }}
                step={1}
                marks
                min={1}
                max={5}
                value={temperature}
                onChange={handleSliderChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => SLIDER_LABELS[value - 1].label}
                sx={{
                  mt: { xs: 1, md: 0 }
                }}
              />
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  )
}
